import { useState, useRef, useCallback } from 'react';

/**
 * React hook for managing controlled state.
 *
 * This hook is useful for managing state that is controlled by external factors,
 * such as props or other components. It provides a way to update the state and
 * handle changes to the state.
 *
 * @param value The current value of the state.
 * @param defaultValue The default value of the state.
 * @param onChange The function to call when the state changes.
 * @typeParam T The type of the value being managed.
 * @returns A tuple containing:
 * - `value`: The current value of the state.
 * - `setValue`: A function to update the state.
 *
 * @category Hooks
 * @example
 * ```tsx
 * const [value, setValue] = useControlledState(0, 0, (value) => {
 *   console.log(value);
 * });
 * ```
 */
function useControlledState(value, defaultValue, onChange) {
    const [stateValue, setStateValue] = useState(value || defaultValue);
    const ref = useRef(value !== undefined);
    const wasControlled = ref.current;
    const isControlled = value !== undefined;
    const stateRef = useRef(stateValue);
    if (wasControlled !== isControlled) {
        console.warn(`WARN: A component changed from ${wasControlled ? 'controlled' : 'uncontrolled'} to ${isControlled ? 'controlled' : 'uncontrolled'}.`);
    }
    ref.current = isControlled;
    const setValue = useCallback((value) => {
        const onChangeCaller = (value) => {
            if (onChange) {
                if (!Object.is(stateRef.current, value)) {
                    onChange(value);
                }
            }
            if (!isControlled) {
                stateRef.current = value;
            }
        };
        if (value instanceof Function) {
            const updateFunction = (oldValue) => {
                const interceptedValue = value(isControlled ? stateRef.current : oldValue);
                onChangeCaller(interceptedValue);
                if (!isControlled) {
                    return interceptedValue;
                }
                return oldValue;
            };
            setStateValue(updateFunction);
        }
        else {
            if (!isControlled) {
                setStateValue(value);
            }
            onChangeCaller(value);
        }
    }, [isControlled, onChange]);
    // If a controlled component's value prop changes, we need to update stateRef
    if (isControlled) {
        stateRef.current = value;
    }
    else {
        value = stateValue;
    }
    return [value, setValue];
}

export { useControlledState as default };
//# sourceMappingURL=useControlledState.js.map
